/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { LoggingService } from '../../../logging/logic/logging.service';
import { ClientState, SystemState } from 'src/app/_model/client-state';
import { BehaviorSubject } from 'rxjs';
import { ClientStateService } from '../client-state.service';
import { ClientStateDaoService } from '../../dao/client-state-dao.service';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { AppParameter } from 'src/app/_model/app-parameter';

import { Place } from 'src/app/_model/place';
import {
  isPlaceTypeHomeLoc,
  isPlaceTypeWorkLoc,
  PlaceType,
} from 'src/app/_model/placetype';
import { IMSId, IMSObjectType, IMSObject } from 'src/app/_model/ims-object';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { EmployeeService } from 'src/app/_services/employee/logic/employee.service';
import { Servicejob, ServicejobStatus } from 'src/app/_model/servicejob';

import { App, AppState } from '@capacitor/app';
import { Branch } from 'src/app/_model/branches';
import { BranchesService } from 'src/app/_services/branches/logic/branches.service';
import { Tour, TourGPSStatus } from 'src/app/_model/tour';
import { TourService } from 'src/app/_services/tour/logic/tour.service';
import { JobSectionsService } from 'src/app/_services/job-section/logic/job-section.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { PreferencesService } from 'src/app/_services/preferences/preferences.service';
import { APILevel } from 'src/app/_services/sync/api-level';
import { TourStopsService } from 'src/app/_services/tour-stop/logic/tour-stop.service';
import { DeficiencyTicketsService } from 'src/app/_services/deficiency-ticket/logic/deficiency-tickets.service';
import { EventService, IMSEventContentTypeHook, IMSEventMessageType, IMSEventSourceType, IMSInternalEventHook } from 'src/app/_services/event/event.service';

/**
 * Responsible for managing the client-local state of the app (formerly known as DroidState in the Android app....)
 */
@Injectable()
export class GeneralClientStateService implements ClientStateService {
  // Public Member
  public theState = new BehaviorSubject<ClientState>(undefined);
  // Non Persistent Client States
  public tabBarEnabled$ = new BehaviorSubject<Boolean>(true);

  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // private
  private temporaryClientStateServicejob: Servicejob;
  private temporaryClientStateTargetServicejob: Servicejob;
  private temporaryClientStateLastKnownServicejob: Servicejob;
  private temporaryClientStateTour: Tour;

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private clientStateDao: ClientStateDaoService,
    private appParameter: AppParameterService,
    private servicejobsService: ServicejobsService,
    private tourService: TourService,
    private empService: EmployeeService,
    private preferences: PreferencesService,
    private tourStopsService: TourStopsService,
    private branchesService: BranchesService,
    private deficiencyTicketService: DeficiencyTicketsService,
    private eventService: EventService,
  ) {
    this.initialize();
    this.appParameter.appParameters$.subscribe({
      next: (parameters) => this.resetStateOnNewDate(parameters),
    });

    App.addListener('appStateChange', async (state: AppState) => {
      // state.isActive contains the active state
      if (state.isActive) {
        await this.resetStateOnNewDate(
          this.appParameter.appParameters$.getValue()
        );
      }
    });
  }

  // ClientStateService
  public getImsObjectForForm(targetRef: IMSObjectType, id?: IMSId): IMSObject {
    this.logger.log(
      this.constructor.name,
      '- looking for current obj with reference',
      targetRef,
      id
    );
    if (targetRef === 'ims/ServiceJob') {
      let sjId;
      if (id != undefined && id?.objRef === targetRef) {
        sjId = id;
      } else {
        sjId = this.theState.getValue().serviceJobId;
      }
      this.logger.log(
        this.constructor.name,
        '- looking for servicejob with',
        sjId?.imsId,
        sjId?.secondaryId,
        sjId?.objRef
      );
      return this.servicejobsService.getServiceJobById(sjId);
    } else if (targetRef === 'ims/Employee') {
      return this.empService.me$.getValue();
    } else if (targetRef === 'ims/Tour') {
      let tourId;
      if (id && id.objRef === targetRef) {
        tourId = id;
      } else {
        tourId = this.theState.getValue().tourId;
      }
      return this.tourService.getTourById(tourId);
    } else if (targetRef === 'ims/TourStop') {
      let tourStopId: IMSId;
      if (id && id.objRef === targetRef) {
        this.logger.log(
          this.constructor.name,
          '- provided id is a of type tourstop use this',
          id
        );
        tourStopId = id;
      } else {
        this.logger.log(
          this.constructor.name,
          '- lookup tourstop in clientstate',
          this.theState.getValue(),
          this.theState.getValue()?.tourStopId
        );
        tourStopId = this.theState.getValue().tourStopId;
      }

      this.logger.log(
        this.constructor.name,
        '- search for tourstop with id',
        tourStopId
      );
      const ts = this.tourStopsService.getTourStopById(tourStopId);
      this.logger.log(this.constructor.name, '- found for tourstop', ts);
      return ts;
    } else if (targetRef === 'ims/Branch') {
      let branchId;
      if (id && id.objRef === targetRef) {
        branchId = id;
      } else {
        branchId = this.theState.getValue().branchId;
      }
      return this.branchesService.getBranchById(branchId);
    } else if (targetRef === 'ims/DeficiencyTicket') {
      let dtId: IMSId;
      if (id && id.objRef === targetRef) {
        dtId = id;
      } else {
        dtId = this.theState.getValue().deficiencyTicketId;
      }
      return this.deficiencyTicketService.getDeficiencyTicketById(dtId);
    }
    return undefined;
  }

  public async startWorking() {
    let state = this.theState.value;
    state.setSystemState(SystemState.WORKSTARTED);
    state.customWorkTypeId = undefined;
    await this.setState(state);
    await this.sendInternalHookEvent(IMSInternalEventHook.SOW);
  }

  public async stopWorking() {
    let state = this.theState.value;

    if (state.serviceJobId) {
      this.stopServicejob();
    }
    state.setSystemState(SystemState.NONE);
    state.customWorkTypeId = undefined;
    state.place = undefined;
    state.targetPlace = undefined;
    state.targetServiceJobId = undefined;
    state.targetBranchId = undefined;
    state.branchId = undefined;

    if (
      this.appParameter.appParameters$.getValue()[
        'imsmc.force.trip.close.on.eow'
      ] == true
    ) {
      this.logger.log(
        this.constructor.name,
        'BTripDebug: end active Btrip as we should force it on AE'
      );
      state.activeBtrip = false;
      state.dpFlag = undefined;
    }
    await this.setState(state);
    await this.sendInternalHookEvent(IMSInternalEventHook.EOW);
  }
  public async startPause() {
    let state = this.theState.value;
    state.setSystemState(SystemState.PAUSESTARTED);
    state.customWorkTypeId = undefined;
    await this.setState(state);
    await this.sendInternalHookEvent(IMSInternalEventHook.SOP);
  }
  public async stopPause() {
    let state = this.theState.value;
    // If a servicejob is available we go back to the servicejob
    // otherwise we are only working
    if (state.serviceJobId) {
      state.setSystemState(SystemState.SERVICEJOBSTARTED);
    } else {
      state.setSystemState(SystemState.WORKSTARTED);
    }
    state.customWorkTypeId = undefined;
    await this.setState(state);
    await this.sendInternalHookEvent(IMSInternalEventHook.EOP);
  }
  public async startWait() {
    let state = this.theState.value;
    state.setSystemState(SystemState.WAITSTARTED);
    state.customWorkTypeId = undefined;
    await this.setState(state);
    await this.sendInternalHookEvent(IMSInternalEventHook.SOWAIT);
  }
  public async stopWait() {
    let state = this.theState.value;
    // If a servicejob is available we go back to the servicejob
    // otherwise we are only working
    if (state.serviceJobId) {
      state.setSystemState(SystemState.SERVICEJOBSTARTED);
    } else {
      state.setSystemState(SystemState.WORKSTARTED);
    }
    state.customWorkTypeId = undefined;
    await this.setState(state);
    await this.sendInternalHookEvent(IMSInternalEventHook.EOWAIT);
  }

  public async startServicejob() {
    let state = this.theState.value;
    state.setSystemState(SystemState.SERVICEJOBSTARTED);
    state.customWorkTypeId = undefined;

    let sjobId = this.theState.getValue().serviceJobId;

    this.servicejobsService.updateStatusOfServicejob(
      sjobId,
      ServicejobStatus.STATUS_IN_PROGRESS
    );
    this.logger.log(this.constructor.name, 'start sjob', sjobId);

    this.logger.log(
      this.constructor.name,
      'BTripDebug: we start a sjob so set it as last known location'
    );
    state.lastKnownPlace = {
      sjobId: sjobId,
    };

    await this.setState(state);
    await this.sendInternalHookEvent(IMSInternalEventHook.SOSJOB);
  }

  public async stopServicejob(finish = false, endwork = false) {
    let state = this.theState.value;

    let sjobId = this.theState.getValue().serviceJobId;
    let sjob = this.servicejobsService.getServiceJobById(sjobId);

    this.logger.log(this.constructor.name, 'stop sjob', sjobId, '-', sjob);

    if (finish) {
      this.servicejobsService.updateStatusOfServicejob(
        sjobId,
        ServicejobStatus.STATUS_FINISHED
      );
    } else {
      this.servicejobsService.updateStatusOfServicejob(
        sjobId,
        ServicejobStatus.STATUS_SUSPENDED
      );
    }

    state.setSystemState(SystemState.WORKSTARTED);
    state.customWorkTypeId = undefined;

    state.serviceJobId = undefined;
    state.jobSectionId = undefined;
    await this.setState(state);
    await this.sendInternalHookEvent(IMSInternalEventHook.EOSJOB);
  }
  public async startTravel(
    id?: IMSId,
    location?: Place,
    branchId?: IMSId
  ): Promise<void> {
    let state = this.theState.value;
    state.setSystemState(SystemState.TRAVEL);
    state.customWorkTypeId = undefined;

    state.serviceJobId = undefined;
    state.jobSectionId = undefined;
    state.branchId = undefined;
    if (id) {
      state.targetServiceJobId = id;
    } else if (location) {
      this.logger.log(
        this.constructor.name,
        'PT Debug set targetPlace to',
        location
      );
      state.targetPlace = location;
    } else if (branchId) {
      state.targetBranchId = branchId;
    }

    // JB20211109: If we start a travel from DO or WO we start an btrip
    if (
      state.place &&
      (isPlaceTypeHomeLoc(state.place.placeType) ||
        isPlaceTypeWorkLoc(state.place.placeType))
    ) {
      this.logger.log(
        this.constructor.name,
        'BTripDebug: start Btrip as we depart from DO or WO',
        state.place.title
      );
      state.activeBtrip = true;
    }

    // JB20200117: APP4-273 if we are departing from the current place we
    // need to reset the current place.
    // If the destination would be another place it wouldnt be a problem
    // as the place will get overriden. But if we are on DO and travel to
    // a servicejob, DO wouldnt get overwritten.
    state.place = undefined;
    this.logger.log(this.constructor.name, '- Start Travel', state);
    await this.setState(state);
    await this.sendInternalHookEvent(IMSInternalEventHook.SOTRAVEL);
  }

  public async stopTravel() {
    let state = this.theState.value;

    if (state.targetServiceJobId) {
      state.serviceJobId = state.targetServiceJobId;
      state.setSystemState(SystemState.SERVICEJOBSTARTED);
      this.logger.log(
        this.constructor.name,
        'BTripDebug: arrived at sjob set it as last known place',
        state.serviceJobId
      );
      state.lastKnownPlace = {
        sjobId: state.serviceJobId,
      };
    } else if (state.targetBranchId) {
      state.branchId = state.targetBranchId;
      state.setSystemState(SystemState.WORKSTARTED);
      this.logger.log(
        this.constructor.name,
        'BTripDebug: arrived at branch set it as last known place',
        state.branchId
      );
      state.lastKnownPlace = {
        branchId: state.branchId,
      };
    } else {
      state.place = new Place(state.targetPlace);
      state.setSystemState(SystemState.WORKSTARTED);
      this.logger.log(
        this.constructor.name,
        'BTripDebug: arrived at place set it as last known place',
        state.place.title
      );
      state.lastKnownPlace = {
        place: state.place,
      };

      if (
        state.place &&
        (isPlaceTypeHomeLoc(state.place.placeType) ||
          isPlaceTypeWorkLoc(state.place.placeType))
      ) {
        this.logger.log(
          this.constructor.name,
          'BtripDebug: arrived at DO or WO so close active Btrip'
        );
        state.activeBtrip = false;
        state.dpFlag = undefined;
      }
    }
    state.customWorkTypeId = undefined;
    state.targetServiceJobId = undefined;
    state.targetBranchId = undefined;
    state.targetPlace = undefined;
    this.logger.log(this.constructor.name, '- Stop Travel', state);
    await this.setState(state);
    await this.sendInternalHookEvent(IMSInternalEventHook.EOTRAVEL);
  }

  public async startCustomWorkType(id?: IMSId): Promise<void> {
    let state = this.theState.value;
    state.setSystemState(SystemState.CUSTOMWORKTYPE);
    state.customWorkTypeId = id;
    await this.setState(state);
  }

  public async stopCustomWorkType(): Promise<void> {
    let state = this.theState.value;
    // If a servicejob is available we go back to the servicejob
    // otherwise we are only working
    if (state.serviceJobId) {
      state.setSystemState(SystemState.SERVICEJOBSTARTED);
    } else {
      state.setSystemState(SystemState.WORKSTARTED);
    }
    state.customWorkTypeId = undefined;
    await this.setState(state);
  }

  public async updatePlace(placeType: PlaceType, otherText?: string) {
    let state = this.theState.value;
    if (placeType) {
      state.place = new Place({ placeType: placeType, otherText: otherText });
    } else {
      state.place = undefined;
    }

    this.logger.log(
      this.constructor.name,
      'BTripDebug: update current place so set it as last known Location',
      state.place
    );

    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setServicejobId(id: IMSId): Promise<void> {
    let state = this.theState.value;
    state.serviceJobId = id;
    this.logger.log(
      this.constructor.name,
      'BtripDebug: set new servicejob id so use it as last known location',
      id
    );
    if (id) {
      state.lastKnownPlace = {
        sjobId: state.serviceJobId,
      };
    }
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);

    const lastKnownJobSectionId =
      await this.preferences.getLastKnownJobSectionForServiceJob(id);
    if (lastKnownJobSectionId) {
      state.jobSectionId = lastKnownJobSectionId;
    }

    await this.clientStateDao.saveState(state);
  }

  public async setBranchId(id: IMSId) {
    let state = this.theState.value;
    state.branchId = id;
    this.logger.log(
      this.constructor.name,
      'BtripDebug: set new branch id so use it as last known location',
      id
    );
    if (id) {
      state.lastKnownPlace = {
        branchId: state.branchId,
      };
    }
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setJobSectionId(id: IMSId) {
    let state = this.theState.value;
    state.jobSectionId = id;

    if (state.serviceJobId) {
      await this.preferences.setLastKnownJobSectionForServiceJob(
        state.serviceJobId,
        id
      );
    }

    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setDeficiencyTicketId(id: IMSId): Promise<void> {
    let state = this.theState.value;
    state.deficiencyTicketId = id;

    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setDpFlag(dpFlag: 'dpp' | 'dpd') {
    let state = this.theState.value;
    state.dpFlag = dpFlag;
    this.logger.log(
      this.constructor.name,
      'BtripDebug: set new dpflag',
      dpFlag
    );
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setTourId(id: IMSId) {
    let state = this.theState.value;
    state.tourId = id;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setTemporarySelectedTourId(id: IMSId) {
    let state = this.theState.value;
    state.temporarySelectedTourId = id;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setTourIdFromTemporarySelectedTourId() {
    let state = this.theState.value;
    state.tourId = state.temporarySelectedTourId;
    state.temporarySelectedTourId = undefined;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setTourStopId(id: IMSId) {
    let state = this.theState.value;
    state.tourStopId = id;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setStockRef(id: IMSId) {
    let state = this.theState.value;
    state.stockRef = id;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public enableTabBar() {
    this.tabBarEnabled$.next(true);
  }

  public disableTabBar() {
    this.tabBarEnabled$.next(false);
  }

  public async setLastUsedCarId(carId: IMSId) {
    let state = this.theState.value;
    state.lastUsedCarId = carId;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setLastUsedTrailerId(trailerId: IMSId) {
    let state = this.theState.value;
    state.lastUsedTrailerId = trailerId;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setCurrentDayIsHomeOffice(isHomeOffice: boolean) {
    let state = this.theState.value;
    state.currentDayIsHomeOfficeDay = isHomeOffice;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setCurrentDayExternalStay(externalStay: boolean) {
    let state = this.theState.value;
    if (state == null) {
      return;
    }
    state.currentDayExternalStay = externalStay;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setLastDayExternalStay(externalStay: boolean) {
    let state = this.theState.value;
    if (state == null) {
      return;
    }
    state.lastDayExternalStay = externalStay;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setCurrentTeamLeader(empId: IMSId): Promise<void> {
    let state = this.theState.value;
    state.currentTeamLeader = empId;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async setCurrentTourBtripStop(stopId: IMSId): Promise<void> {
    let state = this.theState.value;
    state.currentTourBtripStop = stopId;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  public async addActiveTrackedTour(tourid: number): Promise<void> {
    let state = this.theState.value;
    const trackedTours = state.activeTrackedTours;
    if (trackedTours.has(tourid)) {
      return;
    }
    trackedTours.add(tourid);
    state.activeTrackedTours = trackedTours;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }
  public async removeActiveTrackedTour(tourid: number): Promise<void> {
    let state = this.theState.value;
    const trackedTours = state.activeTrackedTours;
    if (!trackedTours.has(tourid)) {
      return;
    }
    trackedTours.delete(tourid);
    state.activeTrackedTours = trackedTours;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }
  public async setIsPassenger(isPassenger: boolean): Promise<void> {
    let state = this.theState.value;
    state.isPassenger = isPassenger;
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  // Resetable

  public async reset() {
    this.logger.log(this.constructor.name, '- Reset State');
    // Clientstate cannot be reset only cleared
    this.clear();
  }

  public async clear() {
    this.logger.log(this.constructor.name, '- Clear State');
    const state = new ClientState();
    try {
      await this.clientStateDao.clear();
      await this.clientStateDao.saveState(state);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- failed to persist new state',
        error?.message
      );
    } finally {
      this.logger.log(this.constructor.name, '- set State to', state);
      this.theState.next(state);
    }
  }

  // Syncable
  public async presync(): Promise<void> {
    this.logger.log(this.constructor.name, 'PreSync');
    // Store References to the IMSObject of the current State
    // which could be local and might be not available anymore
    // after a sync.
    const sjobId = this.theState.getValue().serviceJobId;
    const targetSjobId = this.theState.getValue().targetServiceJobId;
    const lastKnownSjobId = this.theState.getValue().lastKnownPlace
      ? this.theState.getValue().lastKnownPlace.sjobId
      : undefined;
    const tourId = this.theState.getValue().tourId;
    if (sjobId) {
      this.temporaryClientStateServicejob =
        this.servicejobsService.getServiceJobById(sjobId);
    } else {
      this.temporaryClientStateServicejob = undefined;
    }
    if (targetSjobId) {
      this.temporaryClientStateTargetServicejob =
        this.servicejobsService.getServiceJobById(targetSjobId);
    } else {
      this.temporaryClientStateTargetServicejob = undefined;
    }
    if (lastKnownSjobId) {
      this.temporaryClientStateLastKnownServicejob =
        this.servicejobsService.getServiceJobById(lastKnownSjobId);
    } else {
      this.temporaryClientStateLastKnownServicejob = undefined;
    }
    if (tourId) {
      this.temporaryClientStateTour = this.tourService.getTourById(tourId);
    } else {
      this.temporaryClientStateTour = undefined;
    }
  }
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    // Nothing to sync (yet)
  }
  public async postsync(): Promise<void> {
    this.logger.log(
      this.constructor.name,
      'PreSync',
      this.temporaryClientStateServicejob,
      this.temporaryClientStateTargetServicejob,
      this.temporaryClientStateLastKnownServicejob
    );
    // Check if the stored references are still available
    // If they are still available, everything is fine
    // If not we need to try to find the remote version by the secondary key
    // If it could not be found by the secondary key, we readd the current servicejob otherwise the app can
    // not work correctly. (e.g. Workend on a not available sjob like KRM happened if the sjob got removed from the
    // projektplanning)

    // We had a reference before
    if (this.temporaryClientStateServicejob) {
      const sjob = this.servicejobsService.getServiceJobById(
        this.temporaryClientStateServicejob.id
      );

      // Object is not available anymore
      if (!sjob) {
        const remoteServicejob =
          this.temporaryClientStateServicejob.appaccountingnumber != null
            ? this.servicejobsService.servicejobs$.getValue().find((val) => {
                return (
                  this.temporaryClientStateServicejob.appaccountingnumber ===
                    val.appaccountingnumber ||
                  this.temporaryClientStateServicejob.appaccountingnumber ===
                    val.accountingnumber
                );
              })
            : null;

        // A remote object with the same secondary key could be found
        if (remoteServicejob) {
          this.logger.log(
            this.constructor.name,
            'Could reference the clientstates id to a new remote id',
            remoteServicejob.id
          );
          this.setServicejobId(remoteServicejob.id);
        } else {
          // Object could not be found anymore. Add the stored one to keep the app functioning
          this.logger.log(
            this.constructor.name,
            'Readd the referenced servicejob so the app keeps functioning'
          );
          this.servicejobsService.addServicejob(
            this.temporaryClientStateServicejob
          );
        }
      }
    }

    if (this.temporaryClientStateTargetServicejob) {
      const targetSjob = this.servicejobsService.getServiceJobById(
        this.temporaryClientStateTargetServicejob.id
      );

      // Object is not available anymore
      if (!targetSjob) {
        const remoteServicejob = this.servicejobsService.servicejobs$
          .getValue()
          .find((val) => {
            return (
              this.temporaryClientStateTargetServicejob.appaccountingnumber ===
              val.appaccountingnumber
            );
          });

        // A remote object with the same secondary key could be found
        if (remoteServicejob) {
          this.logger.log(
            this.constructor.name,
            'Could reference the clientstates id to a new remote id',
            remoteServicejob.id
          );
          this.setServicejobId(remoteServicejob.id);
        } else {
          // Object could not be found anymore. Add the stored one to keep the app functioning
          this.logger.log(
            this.constructor.name,
            'Readd the referenced servicejob so the app keeps functioning'
          );
          this.servicejobsService.addServicejob(
            this.temporaryClientStateTargetServicejob
          );
        }
      }
    }

    if (this.temporaryClientStateLastKnownServicejob) {
      const lastKnownSjob = this.servicejobsService.getServiceJobById(
        this.temporaryClientStateLastKnownServicejob.id
      );

      // Object is not available anymore
      if (!lastKnownSjob) {
        const remoteServicejob = this.servicejobsService.servicejobs$
          .getValue()
          .find((val) => {
            return (
              this.temporaryClientStateLastKnownServicejob
                .appaccountingnumber === val.appaccountingnumber
            );
          });

        // A remote object with the same secondary key could be found
        if (remoteServicejob) {
          this.logger.log(
            this.constructor.name,
            'Could reference the clientstates id to a new remote id',
            remoteServicejob.id
          );
          const state = this.theState.getValue();
          state.lastKnownPlace.sjobId = remoteServicejob.id;
          this.theState.next(state);
        } else {
          // Object could not be found anymore. Add the stored one to keep the app functioning
          this.logger.log(
            this.constructor.name,
            'Readd the last known place servicejob so the app keeps functioning'
          );
          this.servicejobsService.addServicejob(
            this.temporaryClientStateLastKnownServicejob
          );
        }
      }
    }

    if (this.temporaryClientStateTour) {
      const tour = this.tourService.getTourById(
        this.temporaryClientStateTour.id
      );

      // Object is not available anymore
      if (!tour) {
        // Object could not be found anymore. Add the stored one to keep the app functioning
        this.logger.log(
          this.constructor.name,
          'Readd the tour so the app keeps functioning'
        );
        this.tourService.addTour(this.temporaryClientStateTour);
      }
    }
  }

  public async startSteeringBreak() {
    let state = this.theState.value;
    state.steeringBreakActive = true;
    await this.setState(state);
  }

  public async endSteeringBreak() {
    let state = this.theState.value;
    state.steeringBreakActive = false;
    await this.setState(state);
  }

  public async startRestingBreak() {
    let state = this.theState.value;
    state.restingBreakActive = true;
    await this.setState(state);
  }

  public async endRestingBreak() {
    let state = this.theState.value;
    state.restingBreakActive = false;
    await this.setState(state);
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadState();
      this.logger.log(
        this.constructor.name,
        'Reset temporary states on initialize'
      );
      await this.resetTemporaryStates();
      await this.resetStateOnNewDate(
        this.appParameter.appParameters$.getValue()
      );
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async setState(state: ClientState) {
    this.theState.next(state);
    this.logger.log(this.constructor.name, '- set new State to', state);
    await this.clientStateDao.saveState(state);
  }

  private async loadState() {
    let state: ClientState;
    try {
      state = await this.clientStateDao.loadState();
      if (!state) {
        this.logger.log(
          this.constructor.name,
          '- errornous loaded state - create new State',
          state
        );
        state = new ClientState();
      }
    } catch (error) {
      this.logger.log(
        this.constructor.name,
        '- failed loading state - create new State',
        error?.message
      );
      state = new ClientState();
    } finally {
      this.logger.log(this.constructor.name, '- State loaded - set to', state);
      this.theState.next(state);
    }
  }

  private async resetTemporaryStates() {
    await this.setTemporarySelectedTourId(undefined);
  }

  private async resetStateOnNewDate(parameters: AppParameter) {
    const clientState = this.theState.getValue();
    if (parameters) {
      this.logger.log(
        this.constructor.name,
        '- Based on App Parameter check if reset state on new date is necessary',
        parameters
      );
      if (clientState && clientState.isEditedLastDay()) {
        if (parameters['imsmc.reset.workstate.ondaychange']) {
          this.logger.log(
            this.constructor.name,
            '- Based on App Parameter reset state on new date'
          );
          await this.stopWorking();
        }

        if (clientState?.currentDayIsHomeOfficeDay) {
          this.logger.log(
            this.constructor.name,
            '- On day change always reset currentDayIsHomeOfficeDay'
          );
          await this.setCurrentDayIsHomeOffice(false);
        }

        await this.setLastDayExternalStay(
          clientState?.currentDayExternalStay ?? false
        );
        await this.setCurrentDayExternalStay(false);

        this.logger.log(this.constructor.name, "send SoD Event")
        const eventContent: IMSEventContentTypeHook = {
          kind: IMSEventMessageType.HOOK,
          hook: IMSInternalEventHook.SOD
        }
        this.eventService.sendEvent({
          source: {
            type: IMSEventSourceType.INTERNAL,
            contentType: eventContent
          }
        }, this.appParameter.appParameters$.getValue());
      }
    }
  }

  async initializeCompleted(): Promise<void> {}

  private async sendInternalHookEvent(hook: IMSInternalEventHook) {
    const eventContent: IMSEventContentTypeHook = {
      kind: IMSEventMessageType.HOOK,
      hook: hook
    }
    await this.eventService.sendEvent({
      source: {
        type: IMSEventSourceType.INTERNAL,
        contentType: eventContent
      }
    }, this.appParameter.appParameters$.getValue());
  }
}
